// SAD-431 — the viewer's open actionable shared-information items for the
// Library (C-NOTIF-01/02). Attention is advisory: nothing here gates the
// manual Library flows below it (C-NOTIF-03), and a failed load never blocks
// the page.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover, PopoverContent, PopoverTrigger, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraIconButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { CircleHelp } from 'lucide-react';
import { useAttention, useResolveAttention } from './attentionHooks';
import { errorText } from './reuseHooks';
const ACTION_TEXT = {
    LIBRARY_ITEM_RELEASED: 'Review the released version, then mark it resolved.',
    LIBRARY_ITEM_WITHDRAWN: 'Check where this item was reused, then mark it resolved.'
};
const DEFAULT_ACTION = 'Review this item, then mark it resolved.';
const WHY_TEXT = {
    LIBRARY_ITEM_RELEASED: 'A teammate released a version of an item you own.',
    LIBRARY_ITEM_WITHDRAWN: 'A teammate withdrew a release of an item you own.'
};
const DEFAULT_WHY = 'Something shared with you in the Library changed.';
const HELP_TEXT = 'These items need your attention because something shared with you in the Library changed. ' + 'Each one appears once, no matter how often the source refreshes, and only while you are still ' + 'permitted to see it. Resolving an item clears it from this list and keeps its history. ' + 'You can keep working in the Library whether or not you resolve them.';
export function AttentionPanel() {
    const attention = useAttention();
    const resolve = useResolveAttention();
    const items = attention.data?.items ?? [];
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.libraryAttention.panel,
        children: [
            /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                className: "flex flex-row items-center justify-between space-y-0 pb-2",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraCardTitle, {
                        className: "text-sm",
                        children: [
                            "Needs your attention",
                            ' ',
                            attention.isSuccess && /*#__PURE__*/ _jsx("span", {
                                className: "text-muted-foreground",
                                "data-testid": Testids.libraryAttention.count,
                                children: attention.data.total
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Popover, {
                        children: [
                            /*#__PURE__*/ _jsx(PopoverTrigger, {
                                asChild: true,
                                children: /*#__PURE__*/ _jsx(SpecraIconButton, {
                                    variant: "ghost",
                                    size: "sm",
                                    label: "About attention items",
                                    "data-testid": Testids.libraryAttention.helpButton,
                                    children: /*#__PURE__*/ _jsx(CircleHelp, {
                                        className: "h-4 w-4 text-muted-foreground"
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(PopoverContent, {
                                className: "text-xs",
                                "data-testid": Testids.libraryAttention.helpContent,
                                children: HELP_TEXT
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-2",
                children: [
                    attention.isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        "data-testid": Testids.libraryAttention.loading,
                        children: "Loading…"
                    }),
                    attention.isError && /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs text-danger",
                        "data-testid": Testids.libraryAttention.error,
                        children: [
                            "Could not load attention items: ",
                            errorText(attention.error),
                            ". You can keep working."
                        ]
                    }),
                    resolve.isError && /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs text-danger",
                        "data-testid": Testids.libraryAttention.error,
                        children: [
                            "Could not resolve: ",
                            errorText(resolve.error)
                        ]
                    }),
                    attention.isSuccess && items.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        "data-testid": Testids.libraryAttention.empty,
                        children: "Nothing needs your attention."
                    }),
                    items.map((i)=>/*#__PURE__*/ _jsxs("div", {
                            className: "flex items-start justify-between gap-3 rounded-md border p-2",
                            "data-testid": Testids.libraryAttention.item,
                            "data-attention-id": i.id,
                            "data-notification-type": i.notificationType,
                            "data-source-id": i.sourceId,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-0.5 text-xs",
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "font-medium",
                                            "data-testid": Testids.libraryAttention.itemTitle,
                                            children: i.title
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-muted-foreground",
                                            "data-testid": Testids.libraryAttention.itemWhy,
                                            children: i.body || WHY_TEXT[i.notificationType] || DEFAULT_WHY
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            "data-testid": Testids.libraryAttention.itemAction,
                                            children: ACTION_TEXT[i.notificationType] ?? DEFAULT_ACTION
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "outline",
                                    disabled: resolve.isPending,
                                    "data-testid": Testids.libraryAttention.resolveButton,
                                    onClick: ()=>resolve.mutate(i.id),
                                    children: "Resolve"
                                })
                            ]
                        }, i.id))
                ]
            })
        ]
    });
}
